import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { seoContent } from '@afficheservices/db/schema'
import { eq, and } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { LeadCaptureBlock } from '~/components/lead-capture-block'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { ContentMeta, SourceCitations } from '~/components/content-trust'
import { AuthorByline, AuthorSchema, getAuthorForCategory } from '~/components/eeat-author'
import {
  CheckCircle, Circle, AlertTriangle, Euro,
  Clock, FileText, Lightbulb,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

// ── Checklist definitions ──
const CHECKLISTS: Record<string, {
  title: string
  subtitle: string
  category: string
  duration: string
  budget: string
  steps: Array<{ phase: string; items: Array<{ text: string; important?: boolean; tip?: string }> }>
}> = {
  'renovation-salle-de-bain': {
    title: 'Checklist rénovation salle de bain',
    subtitle: 'Le guide étape par étape pour ne rien oublier',
    category: 'plomberie',
    duration: '2 à 4 semaines',
    budget: '3 000 — 12 000 €',
    steps: [
      { phase: '📋 Avant les travaux', items: [
        { text: 'Définir le budget total (main d\'œuvre + fournitures + imprévus 15 %)', important: true },
        { text: 'Mesurer la pièce (plan coté avec emplacements eau/électricité)' },
        { text: 'Choisir le style et les matériaux (carrelage, robinetterie, meuble vasque)' },
        { text: 'Vérifier la VMC existante (obligatoire en pièce humide)' },
        { text: 'Demander 3 devis détaillés à des artisans vérifiés', important: true, tip: 'Exigez un devis avec main d\'œuvre ET fournitures séparées' },
        { text: 'Vérifier les assurances de l\'artisan (RC Pro + décennale)', important: true },
        { text: 'Prévenir le syndic si copropriété (travaux sur colonnes = parties communes)' },
        { text: 'Simuler les aides MaPrimeRénov\' si changement de production d\'eau chaude' },
        { text: 'Commander les matériaux 2-3 semaines à l\'avance (délais livraison)' },
        { text: 'Prévoir une salle d\'eau temporaire (douche portative, WC disponible)' },
      ]},
      { phase: '🔨 Pendant les travaux', items: [
        { text: 'Dépose de l\'existant (sanitaires, carrelage, faux plafond)' },
        { text: 'Vérification de l\'état des murs et du sol (humidité, structure)' },
        { text: 'Mise aux normes plomberie (alimentation + évacuation)', tip: 'PER multicouche est le standard actuel' },
        { text: 'Mise aux normes électricité (volumes de sécurité NF C 15-100)', important: true },
        { text: 'Étanchéité sous carrelage (SEL — Système d\'Étanchéité Liquide)', important: true, tip: 'Obligatoire en douche à l\'italienne, fortement recommandé ailleurs' },
        { text: 'Pose du carrelage sol et murs' },
        { text: 'Installation des sanitaires (douche/baignoire, WC, vasque)' },
        { text: 'Raccordement et test d\'étanchéité (pression 24h)' },
        { text: 'Peinture plafond et finitions' },
        { text: 'Installation VMC (si nouvelle ou remplacement)' },
      ]},
      { phase: '✅ Après les travaux', items: [
        { text: 'Réception des travaux avec l\'artisan (vérifier chaque point)', important: true },
        { text: 'Tester tous les robinets, la douche, le WC, les évacuations' },
        { text: 'Vérifier l\'étanchéité (pas de fuite sous la douche après 48h)' },
        { text: 'Signer le PV de réception (avec ou sans réserves)' },
        { text: 'Conserver toutes les factures (garantie décennale = 10 ans)', important: true },
        { text: 'Faire la déclaration à l\'assurance habitation (travaux modifiant l\'installation)' },
        { text: 'Demander l\'attestation Consuel si refonte électrique complète' },
        { text: 'Laisser un avis sur l\'artisan pour aider les prochains clients' },
      ]},
    ],
  },
  'renovation-cuisine': {
    title: 'Checklist rénovation cuisine',
    subtitle: 'De la conception à la réception — tout vérifier',
    category: 'renovation',
    duration: '3 à 6 semaines',
    budget: '5 000 — 20 000 €',
    steps: [
      { phase: '📋 Conception et préparation', items: [
        { text: 'Définir le triangle d\'activité (évier — plaque — frigo)', important: true, tip: 'Le triangle optimal mesure 3,60 à 6,60 m de périmètre' },
        { text: 'Mesurer la pièce et dessiner un plan (attention aux arrivées d\'eau et d\'électricité)' },
        { text: 'Choisir entre cuisine équipée et sur mesure (budget déterminant)' },
        { text: 'Prévoir les circuits électriques dédiés (four, plaque, lave-vaisselle)', important: true },
        { text: 'Demander 3 devis (pose + fournitures séparées)', important: true },
        { text: 'Vérifier le mur porteur si ouverture prévue (étude structure obligatoire)', important: true },
        { text: 'Prévenir le syndic si copropriété (colonnes d\'eau et ventilation)' },
        { text: 'Commander les équipements 4-6 semaines à l\'avance (cuisiniste + électroménager)' },
        { text: 'Prévoir un espace repas temporaire (micro-ondes, réfrigérateur portable)' },
      ]},
      { phase: '🔨 Travaux', items: [
        { text: 'Dépose de l\'ancienne cuisine et appareils' },
        { text: 'Ouverture de mur si prévue (IPN + étude structure)', tip: 'Budget ouverture : 3 000-8 000 € avec IPN' },
        { text: 'Mise aux normes plomberie (arrivées/évacuations repositionnées)' },
        { text: 'Mise aux normes électricité — circuits dédiés obligatoires :', important: true },
        { text: '  → 1 circuit 32A pour plaque induction' },
        { text: '  → 1 circuit 20A pour four' },
        { text: '  → 1 circuit 20A pour lave-vaisselle' },
        { text: '  → 6 prises minimum au-dessus du plan de travail (NF C 15-100)' },
        { text: 'Revêtement sol (carrelage, vinyle, parquet stratifié cuisine)' },
        { text: 'Pose des meubles et plan de travail' },
        { text: 'Raccordement électroménager et tests' },
        { text: 'Crédence et finitions (joints silicone, plinthes)' },
      ]},
      { phase: '✅ Réception', items: [
        { text: 'Vérifier l\'alignement de tous les meubles et tiroirs' },
        { text: 'Tester chaque appareil (four, plaque, hotte, lave-vaisselle)' },
        { text: 'Vérifier l\'étanchéité sous évier et lave-vaisselle' },
        { text: 'Tester toutes les prises électriques' },
        { text: 'Signer le PV de réception', important: true },
        { text: 'Conserver les garanties : décennale (artisan) + constructeur (électroménager)' },
      ]},
    ],
  },
  'renovation-appartement': {
    title: 'Checklist rénovation complète appartement',
    subtitle: 'Le méga-guide pour une rénovation sans stress',
    category: 'renovation',
    duration: '2 à 6 mois',
    budget: '800 — 1 800 €/m²',
    steps: [
      { phase: '📋 Phase 1 — Préparation (1-2 mois avant)', items: [
        { text: 'Diagnostic complet : plomb, amiante, électricité, termites', important: true },
        { text: 'Étude de structure si modification de murs (ingénieur obligatoire)', important: true },
        { text: 'Déposer la déclaration préalable si changement de façade' },
        { text: 'Informer le syndic 2 mois avant (planning, horaires, assurance artisan)' },
        { text: 'Demander 3-5 devis détaillés par lot ou en entreprise générale' },
        { text: 'Simuler toutes les aides : MaPrimeRénov\' + CEE + éco-PTZ', important: true },
        { text: 'Prévoir budget imprévus : 10-15 % du total', important: true },
        { text: 'Planifier le relogement temporaire si nécessaire' },
        { text: 'Commander les matériaux longs à livrer (cuisine, fenêtres : 4-8 semaines)' },
      ]},
      { phase: '🔨 Phase 2 — Gros œuvre', items: [
        { text: 'Dépose complète (cloisons, sols, plafonds si nécessaire)' },
        { text: 'Ouvertures et modifications de structure (IPN, linteaux)' },
        { text: 'Nouvelles cloisons (placo, carreau de plâtre)' },
        { text: 'Mise aux normes électricité complète (tableau + circuits)', important: true },
        { text: 'Mise aux normes plomberie (alimentation + évacuations)' },
        { text: 'Chauffage : remplacement radiateurs ou plancher chauffant' },
        { text: 'Isolation (murs, plancher, plafond si nécessaire)' },
        { text: 'VMC (simple flux hygroréglable minimum, double flux idéal)' },
      ]},
      { phase: '🎨 Phase 3 — Second œuvre et finitions', items: [
        { text: 'Revêtements sols (parquet, carrelage, vinyle)' },
        { text: 'Carrelage salles d\'eau (avec étanchéité SEL)' },
        { text: 'Peinture (sous-couche + 2 couches minimum)' },
        { text: 'Pose cuisine et salle de bain' },
        { text: 'Menuiseries intérieures (portes, plinthes)' },
        { text: 'Électricité finition (prises, interrupteurs, luminaires)' },
        { text: 'Nettoyage fin de chantier' },
      ]},
      { phase: '✅ Phase 4 — Réception', items: [
        { text: 'Visite de réception pièce par pièce avec l\'artisan', important: true },
        { text: 'Liste des réserves (défauts à corriger)' },
        { text: 'Signer le PV de réception (avec réserves si nécessaire)' },
        { text: 'Garder 5 % du montant jusqu\'à levée des réserves', important: true, tip: 'Vous avez légalement le droit de retenir 5 % jusqu\'à correction des défauts' },
        { text: 'Archiver toutes les factures (garantie décennale 10 ans)' },
        { text: 'Déclarer les travaux à l\'assurance habitation' },
        { text: 'Demander les attestations de conformité (Consuel si refonte électrique)' },
      ]},
    ],
  },
  'isolation-maison': {
    title: 'Checklist isolation thermique maison',
    subtitle: 'Prioriser les travaux pour maximiser les économies',
    category: 'renovation',
    duration: '1 à 4 semaines (par poste)',
    budget: '5 000 — 30 000 € (rénovation globale)',
    steps: [
      { phase: '📋 Diagnostic', items: [
        { text: 'Faire réaliser un audit énergétique (obligatoire pour MaPrimeRénov\' Sérénité)', important: true, tip: 'Coût : 500-1 500 € — partiellement financé par MaPrimeRénov\'' },
        { text: 'Identifier le DPE actuel de votre logement (A à G)' },
        { text: 'Cartographier les déperditions : toiture (30 %), murs (25 %), fenêtres (15 %), sols (10 %), ventilation (20 %)' },
        { text: 'Vérifier l\'éligibilité aux aides AVANT de signer les devis', important: true },
        { text: 'Trouver un artisan RGE (obligatoire pour les aides)', important: true },
      ]},
      { phase: '🔨 Priorité 1 — Combles et toiture (30 % des pertes)', items: [
        { text: 'Combles perdus : soufflage laine de verre/roche (20-30 €/m², R ≥ 7)' },
        { text: 'Combles aménagés : panneaux laine de bois sous rampants (40-80 €/m²)' },
        { text: 'Vérifier l\'absence d\'amiante dans l\'isolation existante', important: true },
        { text: 'Poser un pare-vapeur côté chaud (obligatoire)' },
        { text: 'Ne pas bloquer la ventilation de la toiture (lame d\'air sous couverture)' },
      ]},
      { phase: '🔨 Priorité 2 — Murs (25 % des pertes)', items: [
        { text: 'ITE (Isolation par l\'Extérieur) : la plus performante (120-200 €/m²)', tip: 'Supprime les ponts thermiques, pas de perte de surface habitable' },
        { text: 'ITI (Isolation par l\'Intérieur) : moins chère (50-80 €/m²), perte de surface' },
        { text: 'Matériaux respirants pour le bâti ancien (laine de bois, chanvre)', important: true },
        { text: 'Ne JAMAIS mettre de polystyrène sur des murs anciens en pierre/brique' },
        { text: 'Traiter les ponts thermiques (tableaux de fenêtres, planchers)' },
      ]},
      { phase: '🔨 Priorité 3 — Fenêtres et ventilation', items: [
        { text: 'Double vitrage minimum (Uw ≤ 1,3), triple en zones froides (Uw ≤ 0,8)' },
        { text: 'VMC hygroréglable type B (minimum après isolation)', important: true, tip: 'JAMAIS isoler sans ventiler — risque de moisissures' },
        { text: 'VMC double flux pour les rénovations globales (récupère 80-90 % de la chaleur)' },
        { text: 'Isolation du plancher bas (si accessible : 20-40 €/m²)' },
      ]},
      { phase: '✅ Résultat attendu', items: [
        { text: 'Gain de 2-3 classes DPE (ex: F → C ou D)' },
        { text: 'Réduction de 40-70 % de la facture de chauffage' },
        { text: 'Aides cumulées : 30-60 % du coût couvert pour les ménages modestes' },
        { text: 'Valorisation du bien : +5-15 % de valeur immobilière' },
      ]},
    ],
  },
  'installation-pompe-a-chaleur': {
    title: 'Checklist installation pompe à chaleur',
    subtitle: 'PAC air/eau, air/air — tout vérifier avant, pendant et après',
    category: 'climatisation',
    duration: '2 à 5 jours',
    budget: '8 000 — 18 000 € (avant aides)',
    steps: [
      { phase: '📋 Avant l\'installation', items: [
        { text: 'Faire réaliser un dimensionnement thermique par l\'installateur', important: true, tip: 'Un surdimensionnement est aussi problématique qu\'un sous-dimensionnement' },
        { text: 'Choisir le type : air/eau (chauffage central) ou air/air (splits)' },
        { text: 'Vérifier que l\'artisan est RGE QualiPAC', important: true },
        { text: 'Simuler les aides : MaPrimeRénov\' (3 000-5 000 €) + CEE (2 000-4 000 €)' },
        { text: 'Déposer la demande MaPrimeRénov\' AVANT de signer le devis', important: true },
        { text: 'Vérifier les règles de copropriété pour le groupe extérieur' },
        { text: 'Vérifier la distance réglementaire avec les voisins (bruit)' },
        { text: 'Prévoir le circuit électrique dédié (disjoncteur 20A ou 32A)' },
      ]},
      { phase: '🔨 Installation', items: [
        { text: 'Pose du groupe extérieur (support anti-vibrations, distance mur ≥ 20 cm)' },
        { text: 'Passage des liaisons frigorifiques (cuivre calorifugé)' },
        { text: 'Raccordement au circuit de chauffage existant (si PAC air/eau)' },
        { text: 'Raccordement électrique avec protection dédiée' },
        { text: 'Mise en service par l\'installateur (charge de fluide, tests)' },
        { text: 'Réglage de la courbe de chauffe (adaptation à votre logement)', important: true },
      ]},
      { phase: '✅ Après l\'installation', items: [
        { text: 'Vérifier le COP (Coefficient de Performance) affiché : doit être ≥ 3', important: true },
        { text: 'Signer le PV de mise en service' },
        { text: 'Envoyer les factures à MaPrimeRénov\' pour recevoir l\'aide' },
        { text: 'Souscrire un contrat d\'entretien annuel (obligatoire pour PAC > 4 kW)', important: true },
        { text: 'Conserver la facture (garantie décennale : 10 ans)' },
        { text: 'Déclarer à l\'assurance habitation (nouveau système de chauffage)' },
      ]},
    ],
  },
  'changement-fenêtres': {
    title: 'Checklist remplacement de fenêtres',
    subtitle: 'Tout vérifier pour des fenêtres performantes et éligibles aux aides',
    category: 'menuiserie',
    duration: '1 à 3 jours (pose)',
    budget: '350 — 1 500 € par fenêtre (fourniture + pose)',
    steps: [
      { phase: '📋 Avant les travaux', items: [
        { text: 'Vérifier le règlement de copropriété (matériau, couleur imposés)', important: true },
        { text: 'Choisir le matériau : PVC (économique), alu (moderne), bois (patrimoine), bois-alu (premium)' },
        { text: 'Choisir le vitrage : double (standard), triple (zones froides/bruyantes)' },
        { text: 'Exiger la certification AEV (Air, Eau, Vent) adaptée à votre zone' },
        { text: 'Vérifier que l\'artisan est RGE (obligatoire pour MaPrimeRénov\')', important: true },
        { text: 'Simuler les aides : MaPrimeRénov\' (40-100 €/fenêtre) + CEE (50-100 €)' },
        { text: 'Demander 3 devis avec détail fourniture + pose séparés' },
        { text: 'Commander 3-6 semaines à l\'avance (fabrication sur mesure)' },
      ]},
      { phase: '🔨 Pendant la pose', items: [
        { text: 'Dépose soignée de l\'ancienne fenêtre (protection du mur)', tip: 'Sur pierre tendre (tuffeau, calcaire), exiger une dépose au ciseau, pas au burineur' },
        { text: 'Vérifier l\'état du dormant existant (pourri = remplacement total)' },
        { text: 'Pose avec calage, nivellement et fixation (chevilles adaptées au support)' },
        { text: 'Joint d\'étanchéité extérieur (mousse PU + mastic silicone)' },
        { text: 'Habillage des tableaux intérieurs et extérieurs' },
        { text: 'Pose de la bavette d\'appui (évacuation eau de pluie)', important: true },
        { text: 'Test d\'ouverture/fermeture de chaque ouvrant' },
      ]},
      { phase: '✅ Réception', items: [
        { text: 'Vérifier l\'étanchéité à l\'eau (asperger au jet si possible)' },
        { text: 'Vérifier l\'absence de courant d\'air (flamme de briquet le long du joint)' },
        { text: 'Tester toutes les poignées et crémones' },
        { text: 'Vérifier l\'esthétique (alignement, finitions des habillages)' },
        { text: 'Signer le PV de réception' },
        { text: 'Conserver la facture + attestation de performance (Uw)' },
        { text: 'Demander le Crédit d\'Impôt / MaPrimeRénov\' avec la facture' },
      ]},
    ],
  },
  'refection-toiture': {
    title: 'Checklist réfection de toiture',
    subtitle: 'Couverture, charpente, isolation — ne rien oublier',
    category: 'toiture',
    duration: '1 à 3 semaines',
    budget: '80 — 200 €/m² (couverture complète)',
    steps: [
      { phase: '📋 Préparation', items: [
        { text: 'Diagnostic de la charpente (insectes xylophages, champignons)', important: true },
        { text: 'Diagnostic amiante si toiture en fibrociment (obligatoire)', important: true },
        { text: 'Déclaration préalable de travaux en mairie', important: true, tip: 'Obligatoire pour tout changement de matériau ou de couleur' },
        { text: 'Vérifier les contraintes ABF si secteur protégé' },
        { text: 'Choisir le matériau adapté à la région (ardoise, tuile canal, tuile plate, zinc)' },
        { text: 'Demander 3 devis avec détail : dépose, charpente, isolation, couverture, zinguerie' },
        { text: 'Vérifier la garantie décennale du couvreur', important: true },
        { text: 'Prévoir l\'isolation (sarking ou sous rampants) en même temps', tip: 'C\'est 30-40 % moins cher de faire isolation + couverture en même temps' },
      ]},
      { phase: '🔨 Travaux', items: [
        { text: 'Installation de l\'échafaudage et protections' },
        { text: 'Dépose de l\'ancienne couverture' },
        { text: 'Traitement/réparation de la charpente si nécessaire' },
        { text: 'Pose de l\'écran sous-toiture (HPV — Haute Perméabilité à la Vapeur)', important: true },
        { text: 'Isolation (sarking par l\'extérieur ou panneaux sous rampants)' },
        { text: 'Pose du lattage/contre-lattage' },
        { text: 'Pose de la couverture (du bas vers le haut)' },
        { text: 'Zinguerie (gouttières, noues, faîtage, solins)' },
        { text: 'Pose des fenêtres de toit si prévues' },
      ]},
      { phase: '✅ Réception', items: [
        { text: 'Vérifier l\'étanchéité (premier jour de pluie)', important: true },
        { text: 'Vérifier les finitions de zinguerie (raccords, descentes EP)' },
        { text: 'Contrôler l\'isolation depuis l\'intérieur (pas de jour visible)' },
        { text: 'Signer le PV de réception' },
        { text: 'Conserver la facture (garantie décennale 10 ans)' },
        { text: 'Souscrire une assurance dommages-ouvrage si > 30 000 €', tip: 'Non obligatoire pour les particuliers mais FORTEMENT recommandé' },
      ]},
    ],
  },
  'mise-aux-normes-electrique': {
    title: 'Checklist mise aux normes électrique',
    subtitle: 'NF C 15-100 — tout ce qu\'il faut vérifier',
    category: 'electricite',
    duration: '3 à 10 jours',
    budget: '3 000 — 10 000 € (appartement)',
    steps: [
      { phase: '📋 Diagnostic', items: [
        { text: 'Faire réaliser un diagnostic électrique (obligatoire si installation > 15 ans)', important: true },
        { text: 'Identifier les points de non-conformité (rapport du diagnostiqueur)' },
        { text: 'Prioriser : sécurité des personnes > confort > esthétique' },
        { text: 'Demander 3 devis avec référence à la norme NF C 15-100' },
        { text: 'Vérifier que l\'électricien a l\'attestation Consuel', important: true },
      ]},
      { phase: '🔨 Travaux prioritaires (sécurité)', items: [
        { text: 'Tableau électrique aux normes : disjoncteurs divisionnaires (pas de fusibles)', important: true },
        { text: 'Interrupteurs différentiels 30mA (protection des personnes)', important: true },
        { text: 'Mise à la terre complète (piquet + liaison équipotentielle salle de bain)' },
        { text: 'Circuits spécialisés : plaque (32A), four (20A), lave-linge (20A), lave-vaisselle (20A)' },
        { text: 'Respect des volumes en salle de bain (NF C 15-100 section 701)', important: true },
        { text: 'Remplacement des câbles en textile par des câbles sous gaine ICTA' },
      ]},
      { phase: '🔨 Travaux de confort', items: [
        { text: 'Nombre de prises par pièce (NF C 15-100 : 5 en séjour, 6 en cuisine, 3 en chambre)' },
        { text: 'Points lumineux (1 par pièce minimum, commandés depuis l\'entrée)' },
        { text: 'Prise communication RJ45 dans chaque pièce principale' },
        { text: 'Parafoudre si zone orageuse (recommandé partout)' },
        { text: 'Pré-câblage pour borne de recharge VE si parking' },
      ]},
      { phase: '✅ Validation', items: [
        { text: 'Test de l\'installation par l\'électricien (essais de déclenchement)', important: true },
        { text: 'Demande d\'attestation de conformité Consuel', important: true },
        { text: 'Visite du Consuel (si installation neuve ou rénovation complète)' },
        { text: 'Archiver le schéma unifilaire du tableau (dans le coffret)' },
        { text: 'Conserver facture + attestation Consuel (garantie + revente)' },
      ]},
    ],
  },
}

// All available checklists for sitemap and navigation
const CHECKLIST_SLUGS = Object.keys(CHECKLISTS)

export async function loader({ params }: LoaderFunctionArgs) {
  const slug = params.slug ?? ''
  const checklist = CHECKLISTS[slug]

  if (!checklist) throw new Response('Checklist non trouvée', { status: 404 })

  // Load AI enrichment if available
  const [aiRow] = await db
    .select({
      expertIntro: seoContent.expertIntro,
      localContext: seoContent.localContext,
      faqItems: seoContent.faqItems,
      expertTips: seoContent.expertTips,
      priceAnalysis: seoContent.priceAnalysis,
    })
    .from(seoContent)
    .where(and(
      eq(seoContent.serviceSlug, 'checklist'),
      eq(seoContent.citySlug, slug),
      eq(seoContent.status, 'published'),
    ))
    .limit(1)

  const clean = (t: string) => t.replace(/\\n/g, '\n').replace(/\\t/g, ' ')
  const splitParas = (t: string) => clean(t).split('\n\n').map(p => p.trim()).filter(Boolean)

  const aiContent = aiRow ? {
    expertIntroParas: splitParas(aiRow.expertIntro),
    localContextParas: splitParas(aiRow.localContext),
    faqItems: (typeof aiRow.faqItems === 'string' ? JSON.parse(aiRow.faqItems) : aiRow.faqItems) as Array<{ q: string; a: string }>,
    expertTips: (typeof aiRow.expertTips === 'string' ? JSON.parse(aiRow.expertTips) : aiRow.expertTips) as Array<{ title: string; text: string }>,
    priceAnalysis: clean(aiRow.priceAnalysis),
  } : null

  const totalItems = checklist.steps.reduce((sum, s) => sum + s.items.length, 0)
  const importantItems = checklist.steps.reduce((sum, s) => sum + s.items.filter(i => i.important).length, 0)

  return { slug, checklist, aiContent, totalItems, importantItems, allSlugs: CHECKLIST_SLUGS }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: `${data?.checklist?.title ?? 'Checklist travaux'} — Guide ${new Date().getFullYear()} | AfficheServices` },
  { name: 'description', content: `${data?.checklist?.title} : ${data?.totalItems} points à vérifier, ${data?.checklist?.steps?.length} phases. Budget ${data?.checklist?.budget}. Durée ${data?.checklist?.duration}. Guide gratuit et complet.` },
  { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/checklist/${data?.slug}` },
]

export default function ChecklistPage() {
  const { slug, checklist, aiContent, totalItems, importantItems, allSlugs } = useLoaderData<typeof loader>()
  const author = getAuthorForCategory(checklist.category)

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={aiContent?.faqItems?.map(f => ({ question: f.q, answer: f.a })) ?? []} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Checklists', url: 'https://www.afficheservices.fr/checklist' },
        { name: checklist.title, url: `https://www.afficheservices.fr/checklist/${slug}` },
      ]} />
      <AuthorSchema author={author} articleUrl={`https://www.afficheservices.fr/checklist/${slug}`} />

      <div className="mx-auto max-w-3xl px-4 py-10 lg:px-6">
        <AuthorByline author={author} readTime={Math.ceil(totalItems / 5)} />

        {/* Hero */}
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 text-white">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <FileText className="h-4 w-4" /> Checklist pratique 2026
          </div>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl">{checklist.title}</h1>
          <p className="mt-2 text-sm text-slate-300">{checklist.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-4">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-400" /> {totalItems} points à vérifier
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400" /> {importantItems} points critiques
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Clock className="h-3.5 w-3.5" /> {checklist.duration}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Euro className="h-3.5 w-3.5" /> {checklist.budget}
            </div>
          </div>
        </div>

        {/* AI Expert intro */}
        {aiContent?.expertIntroParas && (
          <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName="France" serviceName={checklist.title.split(' ').slice(1).join(' ')} />
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug={checklist.category} />
          </article>
        )}

        {/* Checklist phases */}
        <div className="mt-8 space-y-6">
          {checklist.steps.map((phase, pi) => (
            <div key={pi} className="rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
              <div className="border-b border-border bg-slate-50 px-5 py-3">
                <h2 className="text-base font-bold text-slate-900">{phase.phase}</h2>
              </div>
              <div className="divide-y divide-border">
                {phase.items.map((item, ii) => (
                  <div key={ii} className={`flex gap-3 px-5 py-3 ${item.important ? 'bg-amber-50/50' : ''}`}>
                    <div className="mt-0.5 shrink-0">
                      {item.important ? (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      ) : (
                        <Circle className="h-4 w-4 text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${item.important ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {item.text}
                      </p>
                      {item.tip && (
                        <p className="mt-1 flex items-start gap-1.5 text-xs text-primary">
                          <Lightbulb className="mt-0.5 h-3 w-3 shrink-0" />
                          {item.tip}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* AI tips */}
        {aiContent?.expertTips && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Conseils d'expert</h2>
            <div className="mt-4 space-y-3">
              {aiContent.expertTips.map((tip, i) => (
                <div key={i} className="rounded-xl bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">{tip.title}</h4>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI price analysis */}
        {aiContent?.priceAnalysis && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <h3 className="text-base font-bold text-emerald-900">Budget et aides</h3>
            <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
          </div>
        )}

        {/* FAQ */}
        {aiContent?.faqItems && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
            <div className="mt-4 space-y-4">
              {aiContent.faqItems.map((faq, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other checklists */}
        <div className="mt-8 rounded-xl border border-border bg-white p-5 shadow-xs">
          <h3 className="text-sm font-bold text-slate-900">Autres checklists</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {allSlugs.filter(s => s !== slug).map((s) => (
              <Link key={s} to={`/checklist/${s}`} className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary">
                {CHECKLISTS[s]?.title?.replace('Checklist ', '') ?? s}
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8">
          <LeadCaptureBlock serviceName={checklist.title.split(' ').pop() ?? 'Travaux'} cityName="votre ville" categorySlug={checklist.category} />
        </div>
      </div>

      <Footer />
    </div>
  )
}
